import React, { useState, useEffect } from 'react';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { useFinanceStatus } from '../hooks/useFinanceStatus';
import { useRoleManagement } from '../hooks/useRoleManagement';
import { Shield, DollarSign, Check, X, Clock, User, Mail, Phone, Calendar, Search, Filter } from 'lucide-react';

const UserManagement: React.FC = () => {
  const { 
    allAdmins, 
    updateAdminStatus, 
    refreshAllAdmins, 
    refreshPendingAdmins 
  } = useAdminStatus();
  
  const { 
    allFinanceUsers, 
    updateFinanceUserStatus, 
    refreshAllFinanceUsers, 
    refreshPendingFinanceUsers 
  } = useFinanceStatus();

  const { changeUserRole } = useRoleManagement();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'APPROVED' | 'DENIED'>('ALL');
  const [roleFilter, setRoleFilter] = useState<'ALL' | 'ADMIN' | 'FINANCE'>('ALL');
  const [loading, setLoading] = useState(false);
  const [showRoleChangeModal, setShowRoleChangeModal] = useState(false);
  const [roleChangeData, setRoleChangeData] = useState<{
    userId: number;
    currentRole: 'ADMIN' | 'FINANCE';
    newRole: 'ADMIN' | 'FINANCE';
    userName: string;
  } | null>(null);

  useEffect(() => {
    refreshAllAdmins();
    refreshPendingAdmins();
    refreshAllFinanceUsers();
    refreshPendingFinanceUsers();
  }, []);

  // Combine all users into a unified list
  const allUsers = [
    ...allAdmins.map(admin => ({ ...admin, role: 'ADMIN' as const })),
    ...allFinanceUsers.map(finance => ({ ...finance, role: 'FINANCE' as const }))
  ];

  // Filter users based on search and filters
  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = 
      user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || user.status === statusFilter;
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const handleStatusUpdate = async (userId: number, newStatus: 'APPROVED' | 'DENIED', role: 'ADMIN' | 'FINANCE') => {
    setLoading(true);
    try {
      if (role === 'ADMIN') {
        await updateAdminStatus(userId, newStatus);
        await refreshAllAdmins();
        await refreshPendingAdmins();
      } else {
        await updateFinanceUserStatus(userId, newStatus);
        await refreshAllFinanceUsers();
        await refreshPendingFinanceUsers();
      }
    } catch (error) {
      console.error('Error updating user status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, currentRole: 'ADMIN' | 'FINANCE', newRole: 'ADMIN' | 'FINANCE') => {
    if (currentRole === newRole) return;
    
    const user = allUsers.find(u => u.id === userId && u.role === currentRole);
    if (!user) return;

    // Show confirmation modal
    setRoleChangeData({
      userId,
      currentRole,
      newRole,
      userName: `${user.firstname} ${user.lastname}`
    });
    setShowRoleChangeModal(true);
  };

  const confirmRoleChange = async () => {
    if (!roleChangeData) return;

    setLoading(true);
    try {
      const user = allUsers.find(u => u.id === roleChangeData.userId && u.role === roleChangeData.currentRole);
      if (!user) throw new Error('User not found');

      // Use the role management hook to change the user's role
      await changeUserRole(roleChangeData.userId, roleChangeData.currentRole, roleChangeData.newRole, {
        auth0ID: user.auth0ID,
        email: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        mobilenumber: user.mobilenumber,
        status: user.status
      });

      // Refresh all user lists
      await Promise.all([
        refreshAllAdmins(),
        refreshPendingAdmins(),
        refreshAllFinanceUsers(),
        refreshPendingFinanceUsers()
      ]);

      setShowRoleChangeModal(false);
      setRoleChangeData(null);

    } catch (error) {
      console.error('Error changing user role:', error);
      alert(`Error changing user role: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'PENDING':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'APPROVED':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'DENIED':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  const getRoleBadge = (role: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1";
    return role === 'ADMIN'
      ? `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`
      : `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
  };

  const pendingCount = allUsers.filter(u => u.status === 'PENDING').length;
  const approvedCount = allUsers.filter(u => u.status === 'APPROVED').length;
  const adminCount = allUsers.filter(u => u.role === 'ADMIN' && u.status === 'APPROVED').length;
  const financeCount = allUsers.filter(u => u.role === 'FINANCE' && u.status === 'APPROVED').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage admin and finance user access, approvals, and role assignments
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900">
              <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Approval</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{pendingCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900">
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Approved</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{approvedCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Admins</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{adminCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Finance Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{financeCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Users
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Status
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'PENDING' | 'APPROVED' | 'DENIED')}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="ALL">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="DENIED">Denied</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filter by Role
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'ALL' | 'ADMIN' | 'FINANCE')}
                className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="ALL">All Roles</option>
                <option value="ADMIN">Admin Users</option>
                <option value="FINANCE">Finance Users</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={`${user.role}-${user.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.role === 'ADMIN' 
                              ? 'bg-blue-100 dark:bg-blue-900' 
                              : 'bg-green-100 dark:bg-green-900'
                          }`}>
                            {user.role === 'ADMIN' ? (
                              <Shield className={`w-5 h-5 text-blue-600 dark:text-blue-400`} />
                            ) : (
                              <DollarSign className={`w-5 h-5 text-green-600 dark:text-green-400`} />
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {user.firstname} {user.lastname}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900 dark:text-white">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        <div>
                          <div>{user.email}</div>
                          <div className="flex items-center text-gray-500 dark:text-gray-400">
                            <Phone className="w-3 h-3 mr-1" />
                            {user.mobilenumber}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getRoleBadge(user.role)}>
                        {user.role === 'ADMIN' ? (
                          <Shield className="w-3 h-3" />
                        ) : (
                          <DollarSign className="w-3 h-3" />
                        )}
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(user.status)}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        <div>
                          <div>{new Date(user.created_at).toLocaleDateString()}</div>
                          <div className="text-xs">{new Date(user.created_at).toLocaleTimeString()}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-2">
                        {user.status === 'PENDING' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleStatusUpdate(user.id, 'APPROVED', user.role)}
                              disabled={loading}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleStatusUpdate(user.id, 'DENIED', user.role)}
                              disabled={loading}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Deny
                            </button>
                          </div>
                        )}
                        
                        {user.status === 'APPROVED' && (
                          <div className="flex flex-col space-y-1">
                            <select
                              value={user.role}
                              onChange={(e) => handleRoleChange(user.id, user.role, e.target.value as 'ADMIN' | 'FINANCE')}
                              disabled={loading}
                              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                            >
                              <option value="ADMIN">Admin</option>
                              <option value="FINANCE">Finance</option>
                            </select>
                            <button
                              onClick={() => handleStatusUpdate(user.id, 'DENIED', user.role)}
                              disabled={loading}
                              className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Revoke
                            </button>
                          </div>
                        )}

                        {user.status === 'DENIED' && (
                          <button
                            onClick={() => handleStatusUpdate(user.id, 'APPROVED', user.role)}
                            disabled={loading}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Change Confirmation Modal */}
      {showRoleChangeModal && roleChangeData && (
        <div className="modal-overlay">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirm Role Change
                </h3>
                <button
                  onClick={() => {
                    setShowRoleChangeModal(false);
                    setRoleChangeData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="mb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`p-2 rounded-full ${
                    roleChangeData.currentRole === 'ADMIN' 
                      ? 'bg-blue-100 dark:bg-blue-900' 
                      : 'bg-green-100 dark:bg-green-900'
                  }`}>
                    {roleChangeData.currentRole === 'ADMIN' ? (
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                    )}
                  </div>
                  <div className="text-2xl text-gray-400">→</div>
                  <div className={`p-2 rounded-full ${
                    roleChangeData.newRole === 'ADMIN' 
                      ? 'bg-blue-100 dark:bg-blue-900' 
                      : 'bg-green-100 dark:bg-green-900'
                  }`}>
                    {roleChangeData.newRole === 'ADMIN' ? (
                      <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    ) : (
                      <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
                    )}
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Are you sure you want to change <strong>{roleChangeData.userName}</strong>'s role 
                  from <strong>{roleChangeData.currentRole}</strong> to <strong>{roleChangeData.newRole}</strong>?
                </p>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Important Notice
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                      This will move the user's record between database tables. The user will need to 
                      sign out and sign back in for the role change to take effect.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRoleChangeModal(false);
                  setRoleChangeData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmRoleChange}
                disabled={loading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Changing...' : 'Confirm Role Change'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
